import firebase from 'firebase/app';
import localforage from 'localforage';
import { readStoredCart } from './cartStorage';

class EfficientDraftMonitor {
  constructor() {
    this.lastCheckTime = 0;
    this.minCheckInterval = 5 * 60 * 1000;
    this.maxCheckInterval = 60 * 60 * 1000;
    this.isChecking = false;
    this.checkQueue = [];
    this.initialized = false;
    this.lastReportHash = null;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.setupSmartTriggers();
    setTimeout(() => this.smartCheck('initial'), 5000);
    console.info('Draft Monitor initialized');
  }

  setupSmartTriggers() {
    window.addEventListener('storage', (e) => {
      if (e.key === 'cart' || e.key === 'customer' || e.key === 'doc') {
        const isRapidUpdate = this.checkIfRapidUpdate();
        if (!isRapidUpdate) {
          this.smartCheck('storage_change');
        }
      }
    });

    window.addEventListener('focus', () => {
      this.smartCheck('focus');
    });

    window.addEventListener('online', () => {
      this.smartCheck('online');
    });

    this.setupIdleDetection();
    this.setupFallbackTimer();
  }

  checkIfRapidUpdate() {
    try {
      const cartTimestamp = localStorage.getItem('cart_last_update');
      if (cartTimestamp) {
        const timeSinceUpdate = Date.now() - parseInt(cartTimestamp);
        return timeSinceUpdate < 2000;
      }
    } catch (error) {
      return false;
    }
    return false;
  }

  setupIdleDetection() {
    let idleTimer;
    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        this.smartCheck('idle');
      }, 10 * 60 * 1000);
    };

    ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach((event) => {
      document.addEventListener(event, resetIdleTimer, true);
    });

    resetIdleTimer();
  }

  setupFallbackTimer() {
    const fallbackCheck = () => {
      this.smartCheck('fallback');
      const nextCheck = 45 * 60 * 1000 + Math.random() * 30 * 60 * 1000;
      setTimeout(fallbackCheck, nextCheck);
    };

    const firstCheck = 45 * 60 * 1000 + Math.random() * 15 * 60 * 1000;
    setTimeout(fallbackCheck, firstCheck);
  }

  smartCheck(trigger) {
    const now = Date.now();

    if (now - this.lastCheckTime < this.minCheckInterval) {
      return;
    }

    if (this.isChecking) {
      if (!this.checkQueue.includes(trigger)) {
        this.checkQueue.push(trigger);
      }
      return;
    }

    this.performCheck(trigger);
  }

  async performCheck(trigger, contextData = {}) {
    this.isChecking = true;
    this.lastCheckTime = Date.now();

    try {
      console.info(
        `[Draft Monitor] Checking drafts (triggered by: ${trigger})`,
      );

      const draftData = await this.getCurrentDraftData(contextData);

      if (this.shouldReport(draftData)) {
        await this.sendDraftReport(draftData, trigger);
      }

      if (this.checkQueue.length > 0) {
        const queuedTriggers = [...this.checkQueue];
        this.checkQueue = [];
        console.info(
          `[Draft Monitor] Processed ${queuedTriggers.length} queued checks`,
        );
      }
    } catch (error) {
      console.error('[Draft Monitor] Check failed:', error);
    } finally {
      this.isChecking = false;
    }
  }

  async getGlobalUserData() {
    try {
      const currentUser = firebase.auth().currentUser;
      if (!currentUser) {
        console.warn('[Draft Monitor] No current user found');
        return { authUser: null, organization: null };
      }

      const userStore = localforage.createInstance({
        name: 'msales',
        storeName: 'userData',
      });

      const cachedUserData = await userStore.getItem(currentUser.uid);

      if (cachedUserData) {
        return {
          authUser: {
            id: cachedUserData.id,
            code: cachedUserData.code,
            uid: currentUser.uid,
          },
          organization: {
            id: cachedUserData.organization?.id,
            code: cachedUserData.organization?.code,
          },
        };
      }

      return {
        authUser: {
          id: null,
          code: null,
          uid: currentUser.uid,
        },
        organization: null,
      };
    } catch (error) {
      console.error('[Draft Monitor] Error getting global user data:', error);
      return { authUser: null, organization: null };
    }
  }

  async getCurrentDraftData(contextData = {}) {
    try {
      const cart = readStoredCart();
      const customer = JSON.parse(localStorage.getItem('customer') || 'null');
      const orderType = JSON.parse(localStorage.getItem('doc') || 'null');

      const { authUser, organization: userOrganization } =
        await this.getGlobalUserData();
      const { currentOrganization } = await this.getCurrentOrganization();

      const finalAuthUser = contextData.authUser || authUser;
      const finalOrganization =
        contextData.organization || currentOrganization || userOrganization;
      const requestedDeliveryDate = contextData.requestedDeliveryDate || null;

      const hasCurrentCart = cart.length > 0;
      const orderInProgress = hasCurrentCart && orderType !== null;

      const cartTotal = cart.reduce((total, item) => {
        const price = item.price || 0;
        const quantity = item.quantity || 0;
        const discount = item.discount || 0;
        const discount2 = item.discount2 || 0;
        const discount3 = item.discount3 || 0;

        let itemTotal = price * quantity;
        if (discount > 0) itemTotal *= 1 - discount / 100;
        if (discount2 > 0) itemTotal *= 1 - discount2 / 100;
        if (discount3 > 0) itemTotal *= 1 - discount3 / 100;

        return total + itemTotal;
      }, 0);

      const cartDrafts = await this.getCartDrafts(finalOrganization);
      const pendingOperations = await this.getPendingOperations();
      const failedCount = await this.getFailedOperations();

      return {
        hasCurrentCart,
        orderInProgress,
        cartTotal: Math.round(cartTotal * 100) / 100,
        currentCart: cart,
        customer,
        orderType,
        requestedDeliveryDate,
        organization: finalOrganization,
        authUser: finalAuthUser,
        itemCount: cart.length,
        failedCount,
        cartDrafts,
        pendingOperations,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error('[Draft Monitor] Error getting draft data:', error);
      return {
        hasCurrentCart: false,
        orderInProgress: false,
        cartTotal: 0,
        customer: null,
        orderType: null,
        requestedDeliveryDate: null,
        organization: null,
        authUser: null,
        cartDrafts: [],
        pendingOperations: { count: 0, oldestTimestamp: null, operations: [] },
      };
    }
  }

  async getCurrentOrganization() {
    try {
      const selectedOrgId = localStorage.getItem('selectedOrganization');

      const currentUser = firebase.auth().currentUser;

      if (!currentUser) {
        return { currentOrganization: null };
      }

      const userStore = localforage.createInstance({
        name: 'msales',
        storeName: 'userData',
      });

      const cachedUserData = await userStore.getItem(currentUser.uid);

      if (cachedUserData?.organization?.id) {
        return {
          currentOrganization: {
            id: cachedUserData.organization.id,
            code: cachedUserData.organization.code,
          },
        };
      }

      if (selectedOrgId) {
        const parsedId = JSON.parse(selectedOrgId);
        return {
          currentOrganization: {
            id: parsedId,
            code: parsedId?.toString() || null,
          },
        };
      }

      return { currentOrganization: null };
    } catch (error) {
      console.error(
        '[Draft Monitor] Error getting current organization:',
        error,
      );
      return { currentOrganization: null };
    }
  }

  async getFailedOperations() {
    try {
      const storeNames = ['orders', 'surveys', 'tasks', 'visits', 'newCustomer'];
      let totalFailed = 0;

      for (const name of storeNames) {
        const failedStore = localforage.createInstance({
          name: `FailedOperations_${name}`,
        });
        const ops = (await failedStore.getItem('operations')) || [];
        totalFailed += ops.length;
      }

      return totalFailed;
    } catch (error) {
      console.error('[Draft Monitor] Error getting failed operations:', error);
      return 0;
    }
  }

  async getPendingOperations() {
    try {
      const operationStore = localforage.createInstance({
        name: 'OfflineOperations_orders',
      });

      const operations = (await operationStore.getItem('operations')) || [];

      if (!Array.isArray(operations) || operations.length === 0) {
        return { count: 0, oldestTimestamp: null, operations: [] };
      }

      const validOperations = operations.filter(
        (op) => op && typeof op === 'object',
      );

      if (validOperations.length === 0) {
        return { count: 0, oldestTimestamp: null, operations: [] };
      }

      const isValidDate = (dateStr) => {
        if (!dateStr) return false;
        const date = new Date(dateStr);
        return date instanceof Date && !isNaN(date.getTime());
      };

      const oldestOperation = validOperations.reduce((oldest, op) => {
        if (!isValidDate(op.createdAt)) return oldest;
        if (!oldest || !isValidDate(oldest.createdAt)) return op;

        const opDate = new Date(op.createdAt);
        const oldestDate = new Date(oldest.createdAt);

        return opDate < oldestDate ? op : oldest;
      }, null);

      const mappedOperations = validOperations.slice(0, 10).map((op) => ({
        id: op.id || null,
        type: op.type || 'unknown',
        customer: op.customer || null,
        orderType: op.orderType || null,
        itemsCount: op.cartItems?.length || 0,
        createdAt: op.createdAt || null,
      }));

      return {
        count: validOperations.length,
        oldestTimestamp: oldestOperation?.createdAt || null,
        operations: mappedOperations,
      };
    } catch (error) {
      console.error('[Draft Monitor] Error getting pending operations:', error);
      return { count: 0, oldestTimestamp: null, operations: [] };
    }
  }

  async getCartDrafts(fallbackOrganization = null) {
    try {
      const cartDraftsStore = localforage.createInstance({
        name: 'CartDrafts',
      });

      const draftsArray = (await cartDraftsStore.getItem('drafts')) || [];
      const recentDrafts = draftsArray.slice(-3);
      const drafts = [];

      const { authUser } = await this.getGlobalUserData();

      for (const draft of recentDrafts) {
        if (draft) {
          const items = draft.cartItems || draft.cartItemsIds || [];
          const total = items.reduce((sum, item) => {
            const price = item.price || item.wholesalePrice || 0;
            const quantity = item.quantity || 0;
            const discount = item.discount || item.discount1 || 0;
            const discount2 = item.discount2 || 0;
            const discount3 = item.discount3 || 0;

            let itemTotal = price * quantity;
            if (discount > 0) itemTotal *= 1 - discount / 100;
            if (discount2 > 0) itemTotal *= 1 - discount2 / 100;
            if (discount3 > 0) itemTotal *= 1 - discount3 / 100;

            return sum + itemTotal;
          }, 0);

          const draftOrganization =
            typeof draft.organization === 'object'
              ? draft.organization
              : fallbackOrganization || null;

          drafts.push({
            id: draft.id,
            customer: {
              id: draft.customer?.id || null,
              code: draft.customer?.code || null,
            },
            orderType: {
              id: draft.orderType?.id || null,
              code: draft.orderType?.code || null,
            },
            requestedDeliveryDate: draft.requestedDeliveryDate || null,
            organization: {
              id: draftOrganization?.id || null,
              code: draftOrganization?.code || null,
            },
            userID: authUser?.id || null,
            userCode: authUser?.code || null,
            items: items.map((item) => ({
              id: item.id,
              quantity: item.quantity,
            })),
            total: Math.round(total * 100) / 100,
            itemsCount: items.length,
            createdAt: draft.createdAt || new Date().toISOString(),
          });
        }
      }

      return drafts.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
    } catch (error) {
      console.error('[Draft Monitor] Error getting cart drafts:', error);
      return [];
    }
  }

  isOlderThan24Hours(timestamp) {
    if (!timestamp) return false;
    const now = new Date();
    const then = new Date(timestamp);
    const hoursDiff = (now - then) / (1000 * 60 * 60);
    return hoursDiff > 24;
  }

  simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return hash.toString(36);
  }

  hashDraftData(data) {
    const key = JSON.stringify({
      itemCount: data.itemCount || 0,
      total: data.cartTotal || 0,
      customerId: data.customer?.id || null,
      orderType: data.orderType?.id || null,
      pendingCount: data.pendingOperations?.count || 0,
      draftsCount: data.cartDrafts?.length || 0,
      oldestPending: data.pendingOperations?.oldestTimestamp || null,
    });
    return this.simpleHash(key);
  }

  shouldReport(draftData) {
    if (!draftData || typeof draftData !== 'object') {
      return false;
    }

    const hasReportableData =
      draftData.hasCurrentCart ||
      draftData.orderInProgress ||
      (Array.isArray(draftData.cartDrafts) &&
        draftData.cartDrafts.length > 0) ||
      (draftData.pendingOperations?.count || 0) > 0 ||
      (draftData.failedCount || 0) > 0;

    if (!hasReportableData) {
      return false;
    }

    const dataHash = this.hashDraftData(draftData);

    if (dataHash === this.lastReportHash) {
      console.log('[Draft Monitor] Data unchanged, skipping report');
      return false;
    }

    this.lastReportHash = dataHash;
    return true;
  }

  async sendDraftReport(draftData, trigger) {
    try {
      const graphqlServer =
        process.env.REACT_APP_GRAPHQL_SERVER || 'http://localhost:5001/graphql';
      const serverBaseUrl = graphqlServer.replace('/graphql', '');
      const apiUrl = `${serverBaseUrl}/api/draft-order-logs`;

      const pendingCount = draftData.pendingOperations?.count || 0;
      const oldestPending =
        draftData.pendingOperations?.oldestTimestamp || null;
      const currentCart = Array.isArray(draftData.currentCart)
        ? draftData.currentCart
        : [];

      const oldestPendingMinutes = oldestPending
        ? Math.floor((Date.now() - new Date(oldestPending)) / (1000 * 60))
        : 0;

      const cartDrafts = Array.isArray(draftData.cartDrafts)
        ? draftData.cartDrafts.slice(0, 3)
        : [];

      const totalPendingValue =
        draftData.pendingOperations?.operations?.reduce(
          (sum, op) => sum + (op.total || 0),
          0,
        ) || 0;

      const reportData = {
        userID: draftData.authUser?.id || null,
        userCode: draftData.authUser?.code || null,
        hasCurrentOrder: draftData.hasCurrentCart || draftData.orderInProgress,
        critical: pendingCount > 5 || oldestPendingMinutes > 24 * 60,
        stats: {
          pending_count: pendingCount,
          failed_count: draftData.failedCount || 0,
          drafts_count: draftData.cartDrafts?.length || 0,
          oldest_pending_minutes: oldestPendingMinutes,
          total_pending_value: totalPendingValue,
          gps_success_rate: null,
          order_in_progress: draftData.orderInProgress || false,
          current_cart_items: draftData.itemCount || 0,
          current_cart_total: draftData.cartTotal || 0,
        },
        orders: {
          current_order:
            draftData.orderInProgress && draftData.hasCurrentCart
              ? {
                  status: 'in_progress',
                  customer: {
                    id: draftData.customer?.id || null,
                    code: draftData.customer?.code || null,
                  },
                  orderType: {
                    id: draftData.orderType?.id || null,
                    code: draftData.orderType?.code || null,
                  },
                  requestedDeliveryDate: draftData.requestedDeliveryDate || null,
                  organization: {
                    id: draftData.organization?.id || null,
                    code: draftData.organization?.code || null,
                  },
                  userID: draftData.authUser?.id || null,
                  userCode: draftData.authUser?.code || null,
                  items: currentCart.map((item) => ({
                    id: item?.id || null,
                    quantity: item?.quantity || 0,
                  })),
                  itemsCount: draftData.itemCount || 0,
                  total: draftData.cartTotal || 0,
                }
              : null,
          current_cart:
            !draftData.orderInProgress && draftData.hasCurrentCart
              ? {
                  status: 'building',
                  customer: {
                    id: draftData.customer?.id || null,
                    code: draftData.customer?.code || null,
                  },
                  orderType: {
                    id: draftData.orderType?.id || null,
                    code: draftData.orderType?.code || null,
                  },
                  requestedDeliveryDate: draftData.requestedDeliveryDate || null,
                  organization: {
                    id: draftData.organization?.id || null,
                    code: draftData.organization?.code || null,
                  },
                  userID: draftData.authUser?.id || null,
                  userCode: draftData.authUser?.code || null,
                  items: currentCart.map((item) => ({
                    id: item?.id || null,
                    quantity: item?.quantity || 0,
                  })),
                  itemsCount: draftData.itemCount || 0,
                  total: draftData.cartTotal || 0,
                }
              : null,
          cart_drafts: cartDrafts,
          pending_operations:
            pendingCount > 0
              ? draftData.pendingOperations.operations.slice(0, 10)
              : [],
        },
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reports: [reportData],
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          origin: window.location.origin,
        }),
      });

      if (response.ok) {
        console.log(`[Draft Monitor] Report sent successfully (${trigger})`);
      } else {
        console.warn(
          `[Draft Monitor] Report failed with status ${response.status}`,
        );
      }
    } catch (error) {
      console.error('[Draft Monitor] Failed to send report:', error);
    }
  }

  manualCheck() {
    this.smartCheck('manual');
  }

  getStatus() {
    return {
      initialized: this.initialized,
      lastCheckTime: this.lastCheckTime,
      isChecking: this.isChecking,
      queuedChecks: this.checkQueue.length,
      nextFallbackCheck: 'Variable (45-75 minutes)',
    };
  }
}

const efficientDraftMonitor = new EfficientDraftMonitor();

if (typeof window !== 'undefined') {
  window.EfficientDraftMonitor = efficientDraftMonitor;
}

export default efficientDraftMonitor;
